import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useRef, useState } from 'react';
import moment from "moment";
import './index.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
);

const LabBarChart = ({
  graphics,
  type = "currency",
  onBarClick,
  indicatorKey,
}) => {
  const chartRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const colors = [
    { bg: 'rgba(146, 133, 249, 0.6)', text: 'rgba(146, 133, 249, 1)' },
    { bg: 'rgba(132, 244, 188, 0.6)', text: 'rgba(18, 206, 112, 1)' },
    { bg: 'rgba(255, 171, 171, 0.6)', text: 'rgba(255, 122, 122, 1)' },
    { bg: 'rgba(205, 91, 232, 0.6)', text: 'rgba(205, 91, 232, 1)' },
    { bg: 'rgba(199, 242, 43, 0.6)', text: 'rgba(138, 173, 11, 1)' },
    { bg: 'rgba(244, 69, 90, 0.6)', text: 'rgba(244, 69, 90, 1)' },
    { bg: 'rgba(20, 173, 204, 0.6)', text: 'rgba(20, 173, 204, 1)' },
    { bg: 'rgba(254, 224, 3, 0.6)', text: 'rgba(203, 179, 1, 1)' },
    { bg: 'rgba(255, 179, 128, 0.6)', text: 'rgba(255, 148, 77, 1)' },
    { bg: 'rgba(0, 204, 146, 0.4)', text: 'rgba(0, 204, 146, 1)' },
    { bg: 'rgba(0, 94, 184, 0.6)', text: 'rgba(0, 94, 184, 1)' },
    { bg: 'rgba(232, 91, 171, 0.6)', text: 'rgba(232, 91, 171, 1)' },
  ];

  const currentMonth = moment().format('YYYY-MM');

  const formatValue = (value) => {
    if (type === 'percent') {
      const numValue = Math.round(parseFloat(value) * 100) / 100;
      if (numValue % 1 === 0) {
        return `${numValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}%`;
      }
      return `${numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    }
    if (type === 'currency') {
      const numValue = Math.round(parseFloat(value) * 100) / 100;
      return `R$ ${numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    const numValue = Math.round(parseFloat(value) * 100) / 100;
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleLegendHover = (index) => {
    setHoveredIndex(index);
    if (chartRef.current) {
      chartRef.current.tooltip.setActiveElements([
        { datasetIndex: 0, index: index }
      ]);
      chartRef.current.update();
    }
  };

  const handleLegendLeave = () => {
    setHoveredIndex(null);
    if (chartRef.current) {
      chartRef.current.tooltip.setActiveElements([]);
      chartRef.current.update();
    }
  };

  const chartData = {
    labels: graphics.map((item) => item.nome),
    datasets: [
      {
        label: 'Valor',
        data: graphics.map((item) => item[indicatorKey]),
        backgroundColor: graphics.map((_, index) => colors[index % colors.length].bg),
        borderColor: graphics.map((_, index) => colors[index % colors.length].text),
        borderWidth: graphics.map((_, index) => (index === hoveredIndex ? 2 : 0)),
        borderRadius: graphics.map((_, index) =>
          index === hoveredIndex
            ? { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 }
            : 0
        ),
        barPercentage: 1,
        categoryPercentage: 0.75,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 32,
      },
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      const newIndex = activeElements.length > 0 ? activeElements[0].index : null;
      setHoveredIndex(newIndex);
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const dataIndex = elements[0].index;
        onBarClick(graphics[dataIndex]?.id, currentMonth);
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grace: "5%",
        grid: {
          color: "rgba(0,0,0,0.06)",
        },
        ticks: {
          font: { size: 11 },
          callback: function(value) {
            if (type === 'percent') return `${value}%`;
            if (type === 'currency') return `R$ ${value.toLocaleString('pt-BR')}`;
            return value;
          },
        },
      },
      x: {
        grid: { display: false },
        ticks: { display: false }
      },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end',
        align: 'top',
        color: (context) => {
          if (context.dataIndex === hoveredIndex) return '#ffffff';
          const color = colors[context.dataIndex % colors.length];
          return color.text || color.bg;
        },
        backgroundColor: (context) => {
          if (context.dataIndex === hoveredIndex) return '#000000';
          return null;
        },
        borderRadius: (context) => {
          if (context.dataIndex === hoveredIndex) return 8;
          return 0;
        },
        padding: (context) => {
          if (context.dataIndex === hoveredIndex) return { top: 4, bottom: 4, left: 8, right: 8 };
          return 0;
        },
        font: (context) => ({
          family: 'Figtree',
          size: context.dataIndex === hoveredIndex ? 13 : 11,
          weight: context.dataIndex === hoveredIndex ? 'bold' : 'bold',
        }),
        formatter: (value, context) => {
          if (context.dataIndex === hoveredIndex) return `✎ ${formatValue(value)}`;

          return formatValue(value);
        },
        listeners: {
          click: (context) => {
            if (onBarClick) {
              onBarClick(graphics[context.dataIndex]?.id, currentMonth);
            }
          }
        }
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    <div className="lab-bar-chart-container">
      <div className="chart-wrapper">
        <Bar ref={chartRef} data={chartData} options={options} plugins={[ChartDataLabels]} />
      </div>
      <div className="custom-legend">
        {graphics.map((item, index) => (
          <div
            key={index}
            className="legend-item"
            onMouseEnter={() => handleLegendHover(index)}
            onMouseLeave={handleLegendLeave}
            onClick={() => {
              if (onBarClick) onBarClick(item.id, currentMonth);
            }}
            style={{ cursor: 'pointer' }}
          >
            <span
              className="legend-color"
              style={{ backgroundColor: colors[index % colors.length].text }}
            />
            <span className="legend-label">{item?.nome}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LabBarChart;
